import { Colors, Intent } from "@blueprintjs/core";
import { ButtonContainer, DataTable, FieldSet, IDataTableColumn, useGridState, useSearchApi } from "nsitools-react";
import * as React from "react";
import { useHistory, useParams } from "react-router";
import { css } from "styled-components";

import { ContactApi, ContactSiegeGridDto, ContactSiegeSearch } from "../../../../../../api";
import { ERoutes } from "../../../../../../AppRouter";
import { AddButton, EditButton, ViewButton } from "../../../../../../components";
import { useApiService, useTl } from "../../../../../../hooks";
import { ETLCodes } from "../../../../../../locales";

export interface ISiegeSocialContactListPageProps { }

export const SiegeSocialContactListPage: React.FunctionComponent<ISiegeSocialContactListPageProps> = props => {
  const { t } = useTl();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const SiegeSocialId = React.useMemo(() => +id, [id]);
  const api = useApiService(ContactApi);
  const tableState = useGridState<any>({
    serverMode: true,
    enablePagination: true,
    enableFilter: true,
    availablePageSizes: [15, 25, 50],
    pageSize: 15,
    sortKeys: { nom: "ASC" }
  });

  const { totalCount } = tableState;

  const searchFunction = React.useCallback(
    (sObj?: ContactSiegeSearch) => {
      sObj.idsiegeSocial = SiegeSocialId;
      return api.contactSearchContactSiege({ ContactSiegeSearch: sObj });
    },
    [api, SiegeSocialId]
  );

  const { loading } = useSearchApi<any, any>({
    searchFunction,
    tableState,
    initialSearch: true
  });

  const onViewClick = React.useCallback(
    (dto: ContactSiegeGridDto) => {
      history.push(`${ERoutes.siegeSocial}/${id}${ERoutes.contacts}/view/${dto.idcontactSiege}`);
    },
    [history, id]
  );

  const onEditClick = React.useCallback(
    (dto: ContactSiegeGridDto) => {
      history.push(`${ERoutes.siegeSocial}/${id}${ERoutes.contacts}/edit/${dto.idcontactSiege}`);
    },
    [history, id]
  );

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        fieldName: "actions",
        autoFitContent: true,
        render: (row: ContactSiegeGridDto) => (
          <ButtonContainer>
            <ViewButton minimal={true} onClick={() => onViewClick(row)} />
            <EditButton minimal={true} onClick={() => onEditClick(row)} />
          </ButtonContainer>
        )
      },
      {
        header: () => t(ETLCodes.Nom),
        fieldName: "nom"
      },
      {
        header: () => t(ETLCodes.Prenom),
        fieldName: "prenom"
      },
      {
        header: () => t(ETLCodes.DateNaissance),
        fieldName: "dateNaissance"
      },
      {
        header: () => t(ETLCodes.Fonction),
        fieldName: "fonction"
      },
      {
        header: () => t(ETLCodes.GsmPro),
        fieldName: "gsm"
      },
      {
        header: () => t(ETLCodes.TelephonePro),
        fieldName: "telephone"
      },
      {
        header: () => t(ETLCodes.EmailPro),
        fieldName: "email"
      }
    ],
    [onEditClick, onViewClick, t]
  );

  const onAddClick = React.useCallback(() => {
    history.push(`${ERoutes.siegeSocial}/${id}${ERoutes.contacts}/checkDoublons`);
  }, [history, id]);

  return (
    <FieldSet
      title={t(ETLCodes.TableResults, { count: totalCount })}
      rightElement={<AddButton onClick={onAddClick} text={t(ETLCodes.General_Add)} intent={Intent.PRIMARY} />}
    >
      <DataTable
        dateFormat="dd/MM/yyyy"
        tableState={tableState}
        loading={loading}
        columns={columns}
        filterMode="OnEnter"
        customizeRowStyle={(row: ContactSiegeGridDto) => css`
          & * {
            color: ${row.principal ? Colors.GREEN3 + " !important" : null};
          }
        `}
      />
    </FieldSet>
  );
};
